import {Component, OnDestroy, OnInit} from '@angular/core';
import {ActivatedRoute, Router, UrlTree} from "@angular/router";
import {RoundService} from "./round.service";
import {combineLatest, distinct, map, Observable, share, Subscription, switchMap, tap} from "rxjs";
import {DatePipe, NgIf} from "@angular/common";
import {TruncatePipe} from "./pipes/truncate.pipe";
import {TimezonePipe} from "./pipes/timezone.pipe";
import {ScorecardComponent} from "./scorecard/scorecard.component";
import {MapComponent} from "./map/map.component";
import {Title} from "@angular/platform-browser";
import {Course, HandicapMode, Round, RoundInfo, RoundSettings, Scorecard} from "./model/round";
import {ScoringMethod, ScoringMethodType} from "./scorecard/model/scoring-method";

@Component({
    selector: 'app-round',
    templateUrl: './round.component.html',
    imports: [
        NgIf,
        TruncatePipe,
        TimezonePipe,
        DatePipe,
        ScorecardComponent,
        MapComponent
    ],
    providers: [TimezonePipe, DatePipe],
    styleUrls: ['./round.component.css']
})
export class RoundComponent implements OnInit, OnDestroy {
    private subscriptions: Subscription[] = [];
    private _round: Observable<Round | null>
    private _currentHoleIndex: Observable<number>

    currentHoleIndex: number = 0;
    isLoading: boolean = true;
    round: Round | null = null;

    get isAvailable(): boolean {
        return this.round != null;
    }

    get info(): RoundInfo | undefined {
        return this.round?.info;
    }

    get settings(): RoundSettings {
        return this.round!.settings;
    }

    get scorecard(): Scorecard {
        return this.round!.scorecard;
    }

    constructor(
        private roundService: RoundService,
        private route: ActivatedRoute,
        private router: Router,
        private titleService: Title,
        private timezonePipe: TimezonePipe,
        private datePipe: DatePipe
    ) {
        this._round = this.route.params.pipe(
            map(params => params["token"]),
            tap(() => this.isLoading = true),
            distinct(),
            switchMap(token => this.roundService.getRound(token)),
            tap(() => this.isLoading = false),
            share()
        )
        let selectedHoleIndex = this.route.fragment.pipe(
            map(fragment => {
                let value = parseInt(fragment ?? "")
                if (!isNaN(value)) {
                    return value - 1
                } else {
                    return 0
                }
            })
        )
        this._currentHoleIndex = combineLatest([this._round, selectedHoleIndex]).pipe(
            map(([round, hole]) => {
                    if (round != null) {
                        return Math.min(Math.max(0, hole), round.settings.course.holes.length - 1)
                    } else {
                        return 0
                    }
                }
            ),
            share()
        )
    }

    ngOnInit(): void {
        this.subscriptions.push(this.setUpPageTitleUpdater())
        this.subscriptions.push(this._currentHoleIndex.subscribe(value => this.currentHoleIndex = value))
        this.subscriptions.push(this._round.subscribe(value => this.round = value))
    }

    ngOnDestroy(): void {
        this.subscriptions.forEach(s => s.unsubscribe())
    }

    onKeyDown(event: KeyboardEvent) {
        // noinspection JSDeprecatedSymbols
        if (event.keyCode === 37) {
            event.preventDefault()
            this.prevHole();
        }
        // noinspection JSDeprecatedSymbols
        if (event.keyCode === 39) {
            event.preventDefault()
            this.nextHole();
        }
    }

    nextHole() {
        this.selectHole(this.currentHoleIndex + 1);
    }

    prevHole() {
        this.selectHole(this.currentHoleIndex - 1);
    }

    getTotalPar(course: Course): number {
        return course.holes.reduce((total, hole) => total + hole.par, 0)
    }

    getHolePar(holeIndex: number): string {
        const hole = this.settings.course.holes[holeIndex];
        if (hole.par != hole.parOverride && hole.parOverride) {
            return hole.par + "/" + hole.parOverride;
        }
        return hole.par.toString();
    }

    getScoringMethod(method: ScoringMethod, handicapMode: HandicapMode): string {
        let net = (handicapMode == HandicapMode.Gross) ? "" : " (Net)"

        switch (method.type) {
            case ScoringMethodType.StrokePlay:
                return `Stroke Play${net}`
            case ScoringMethodType.Stableford:
                return `Stableford${net}`
        }
    }

    private selectHole(hole: number) {
        if (this.round == null) {
            return
        }
        hole = Math.min(Math.max(hole, 0), this.settings.course.holes.length - 1);

        let url: UrlTree = this.router.parseUrl(this.router.url)
        url.fragment = (hole + 1).toString()

        this.router.navigateByUrl(url)
    }

    private setUpPageTitleUpdater(): Subscription {
        return combineLatest([this._round, this._currentHoleIndex])
            .subscribe(([round, currentHoleIndex]) => {
                let info = round?.info
                let settings = round?.settings

                if (info && settings) {
                    const date = this.datePipe.transform(
                        this.timezonePipe.transform(info.startTimestampMs, info.timezoneOffset),
                        'dd MMM yyyy',
                        'UTC'
                    )
                    this.titleService.setTitle(settings.course.name + " - Hole " + (currentHoleIndex + 1) + " - " + date)
                } else if (settings) {
                    this.titleService.setTitle(settings.course.name + " - Hole " + (currentHoleIndex + 1))
                } else {
                    this.titleService.setTitle("Round not available")
                }
            })
    }
}
